/**
 * KinsCircle Phase 6C — Historian Insights Card.
 *
 * Documentary-style read-only card. Renders null when `historianMode`
 * flag is OFF or the historian has no usable signals.
 *
 * EVERY claim cites "Based on N memories from YEAR–YEAR" — no
 * fabrication is possible because the engine only counts existing rows.
 */

import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { BookOpen, Calendar, Repeat, Users, Star } from 'lucide-react';
import {
  isHistorianModeEnabled,
  isRelationshipInsightsEnabled,
} from '@/lib/ai/semanticFeatureFlags';
import { buildHistorianDashboard } from '@/lib/ai/historianEngine';

const formatRange = (from: number | null, to: number | null): string => {
  if (from && to) {
    if (from === to) return `${from}`;
    return `${from}–${to}`;
  }
  if (from) return `${from}`;
  if (to) return `${to}`;
  return 'across your timeline';
};

const HistorianInsightsCard: React.FC = () => {
  if (!isHistorianModeEnabled()) return null;

  const data = useMemo(() => buildHistorianDashboard(), []);
  const showRelationships = isRelationshipInsightsEnabled();

  const totalSignals =
    data.eras.length +
    data.traditions.length +
    data.moments.length +
    (showRelationships ? data.timelines.length + data.crossGen.length : 0);

  if (totalSignals === 0) return null;

  return (
    <section className="py-12 px-4 bg-gradient-to-br from-amber-50/50 via-white to-fuchsia-50/40">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="w-5 h-5 text-amber-700" />
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Family Historian
          </h2>
          <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
            Read-only
          </span>
        </div>
        <p className="text-sm text-slate-600 mb-6 max-w-2xl">
          A warm, documentary-style overview built only from the memories your family
          has already saved. Nothing is invented — every insight cites the memories it
          came from.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Eras */}
          {data.eras.length > 0 && (
            <Card className="bg-white border-amber-200/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-amber-700" />
                  Family Eras
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {data.eras.slice(0, 4).map((e) => (
                  <div
                    key={e.era + e.yearRange.from}
                    className="text-sm border-l-2 border-amber-300 pl-3"
                  >
                    <div className="font-semibold text-slate-900">
                      {e.era}{' '}
                      <span className="font-normal text-slate-500">
                        ({formatRange(e.yearRange.from, e.yearRange.to)})
                      </span>
                    </div>
                    {e.themes.length > 0 && (
                      <div className="text-xs text-slate-600 mt-0.5">
                        Themes: {e.themes.join(', ')}
                      </div>
                    )}
                    {e.topPeople.length > 0 && (
                      <div className="text-xs text-slate-600">
                        People: {e.topPeople.join(', ')}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 mt-1">
                      Based on {e.count} memor{e.count === 1 ? 'y' : 'ies'} from{' '}
                      {formatRange(e.yearRange.from, e.yearRange.to)}.
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Repeated traditions */}
          {data.traditions.length > 0 && (
            <Card className="bg-white border-fuchsia-200/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-1.5">
                  <Repeat className="w-4 h-4 text-fuchsia-700" />
                  Family Traditions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.traditions.slice(0, 5).map((t) => (
                  <div
                    key={t.label}
                    className="flex items-center justify-between text-sm border-b border-slate-100 last:border-0 pb-1.5 last:pb-0"
                  >
                    <div>
                      <div className="font-semibold text-slate-900">{t.label}</div>
                      <div className="text-[10px] text-slate-400">
                        Based on {t.occurrences} memories from{' '}
                        {formatRange(t.yearRange.from, t.yearRange.to)}.
                      </div>
                    </div>
                    <span className="text-xs font-mono text-fuchsia-700">
                      ×{t.occurrences}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Major life moments */}
          {data.moments.length > 0 && (
            <Card className="bg-white border-pink-200/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-pink-700" />
                  Big Moments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.moments.slice(0, 6).map((m) => (
                  <div key={m.memoryId} className="text-sm">
                    <div className="font-semibold text-slate-900">
                      {m.label}
                      {m.year && (
                        <span className="font-normal text-slate-500 ml-1.5">
                          ({m.year})
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 line-clamp-2">
                      {m.caption || '—'}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Relationship timelines */}
          {showRelationships && data.timelines.length > 0 && (
            <Card className="bg-white border-emerald-200/70">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-emerald-700" />
                  Strongest Connections
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.timelines.slice(0, 5).map((t) => (
                  <div
                    key={t.memberKey}
                    className="text-sm flex items-center justify-between gap-3 border-b border-slate-100 last:border-0 pb-1.5 last:pb-0"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 capitalize truncate">
                        {t.memberKey}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        Based on {t.appearances} memor
                        {t.appearances === 1 ? 'y' : 'ies'} from{' '}
                        {formatRange(t.firstYear, t.lastYear)}.
                      </div>
                    </div>
                    <span className="text-xs font-mono text-emerald-700 shrink-0">
                      ×{t.appearances}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <p className="text-[11px] text-slate-400 mt-4">
          The Family Historian only summarizes what already exists in your memories.
          It never guesses dates, relationships, or events that aren't already there.
        </p>
      </div>
    </section>
  );
};

export default HistorianInsightsCard;
